import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, debounce, TextField } from '@mui/material';
import { IOption } from 'components/elements/CustomSelect';

interface CityAutocompleteFieldProps {
	cityOptions: IOption[];
	cityOptionsLoading: boolean;
	hasMoreCityOptions?: boolean;
	initialCityTitle?: string;
	label?: string;
	onCityLoadMore?: () => void;
	onCitySearch: (search: string) => void;
	onChange: (cityId: string) => void;
	placeholder?: string;
	value: string;
}

export const CityAutocompleteField: FC<CityAutocompleteFieldProps> = ({
	cityOptions,
	cityOptionsLoading,
	hasMoreCityOptions = false,
	initialCityTitle = '',
	label = 'Місто',
	onCityLoadMore,
	onCitySearch,
	onChange,
	placeholder = 'Почніть вводити місто',
	value,
}) => {
	const [cityInputValue, setCityInputValue] = useState(initialCityTitle);
	const listboxRef = useRef<HTMLUListElement | null>(null);
	const pendingScrollTopRef = useRef<number | null>(null);
	const previousOptionsLengthRef = useRef(cityOptions.length);

	useEffect(() => {
		setCityInputValue(initialCityTitle);
	}, [initialCityTitle]);

	useEffect(() => {
		if (!value) {
			setCityInputValue('');
		}
	}, [value]);

	const debouncedCitySearch = useMemo(() => debounce((search: string) => onCitySearch(search), 400), [onCitySearch]);

	useEffect(() => {
		return () => {
			debouncedCitySearch.clear();
		};
	}, [debouncedCitySearch]);

	useEffect(() => {
		if (pendingScrollTopRef.current !== null && cityOptions.length > previousOptionsLengthRef.current) {
			const scrollTop = pendingScrollTopRef.current;

			requestAnimationFrame(() => {
				if (listboxRef.current) {
					listboxRef.current.scrollTop = scrollTop;
				}
			});

			pendingScrollTopRef.current = null;
		}

		previousOptionsLengthRef.current = cityOptions.length;
	}, [cityOptions.length]);

	const selectedCityOption = cityOptions.find((option) => option.value === value) || (value && initialCityTitle
		? { value, title: initialCityTitle }
		: null);

	return (
		<Autocomplete
			options={cityOptions}
			value={selectedCityOption}
			inputValue={cityInputValue}
			loading={cityOptionsLoading}
			filterOptions={(options) => options}
			noOptionsText={cityInputValue.trim() ? 'Не знайдено' : placeholder}
			componentsProps={{
				popper: {
					sx: {
						width: 'min(560px, calc(100vw - 32px)) !important',
					},
				},
			}}
			ListboxProps={{
				ref: (node: HTMLUListElement | null) => {
					listboxRef.current = node;
				},
				onScroll: (event) => {
					if (!onCityLoadMore) {
						return;
					}

					const listboxNode = event.currentTarget;
					const isNearBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 16;

					if (isNearBottom && hasMoreCityOptions && !cityOptionsLoading) {
						pendingScrollTopRef.current = listboxNode.scrollTop;
						onCityLoadMore();
					}
				},
			}}
			getOptionLabel={(option) => option.title}
			isOptionEqualToValue={(option, nextValue) => option.value === nextValue.value}
			onChange={(_, nextValue) => {
				onChange(nextValue?.value || '');
				setCityInputValue(nextValue?.title || '');
			}}
			onInputChange={(_, nextValue, reason) => {
				setCityInputValue(nextValue);

				if (reason === 'input' || reason === 'clear') {
					debouncedCitySearch(nextValue);
				}

				if (reason === 'clear') {
					onChange('');
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={placeholder}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{cityOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
